<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { formatPhoneNumber } from '@Helpers';
import type { FieldSchema } from '../../../types';
import { checkDuplicatePersonService } from '../../../services';
import useMFieldContact from './useMFieldContact';

interface PersonResult {
  _id: { $oid: string };
  addresses: string;
  full_name: string;
  id: string;
}

const props = withDefaults(
  defineProps<{
    error?: string;
    field: FieldSchema;
    placeholder?: string;
    required?: boolean;
  }>(),
  {
    placeholder: 'components.forms.placeholder.common',
  }
);

const { contactData, loadingContactData, personId, showPhoneCard, showEmailCard } = useMFieldContact();

let requestCounter = 0;
let checkTimeoutId: ReturnType<typeof setTimeout> | null = null;
let pendingBlurCheck: 'phone' | 'email' | null = null;
let isSelectingFromModal = false;

const ROWS_PER_PAGE = 9;

const model = defineModel<string | undefined>();
const results = ref<{ phone: PersonResult[]; email: PersonResult[] }>({ phone: [], email: [] });
const duplicateTotal = ref(0);
const duplicateFirst = ref(0);
const lastDuplicateValueByField = ref<{ phone: string; email: string }>({ phone: '', email: '' });
const isCheckingDuplicate = ref(false);
const lastCheckedValue = ref<string>('');

onMounted(() => resetComponentState());
onUnmounted(() => clearChecksAndPendingRequests());

watch(model, () => checkForPendingBlurCheck());

function checkForPendingBlurCheck() {
  const userIsInteracting = isSelectingFromModal || showPhoneCard.value || showEmailCard.value;
  if (userIsInteracting) return;

  const userIsTyping = pendingBlurCheck && model.value;
  if (userIsTyping) {
    const fieldToCheck = pendingBlurCheck!;
    pendingBlurCheck = null;
    handleDuplicateCheck(fieldToCheck);
  }
}

function clearChecksAndPendingRequests() {
  if (checkTimeoutId) {
    clearTimeout(checkTimeoutId);
    checkTimeoutId = null;
  }

  requestCounter++;
  pendingBlurCheck = null;
}

function resetComponentState() {
  personId.value = undefined;
  isCheckingDuplicate.value = false;
  lastCheckedValue.value = '';
  isSelectingFromModal = false;
  showPhoneCard.value = false;
  showEmailCard.value = false;
  results.value = { phone: [], email: [] };
  duplicateTotal.value = 0;
  duplicateFirst.value = 0;
  lastDuplicateValueByField.value = { phone: '', email: '' };
  pendingBlurCheck = null;
  loadingContactData.value = false;

  if (!checkTimeoutId) return;

  clearTimeout(checkTimeoutId);
  checkTimeoutId = null;
}

function handleBlur(field: 'phone' | 'email') {
  const ongoingCheckOrInteraction =
    isCheckingDuplicate.value || isSelectingFromModal || showPhoneCard.value || showEmailCard.value;
  if (ongoingCheckOrInteraction) return;

  pendingBlurCheck = field;

  // NOTE: This timeout is for handling browser event cycles.
  const blurCheckTimeout = 2;
  setTimeout(() => {
    if (pendingBlurCheck !== field) return;

    pendingBlurCheck = null;
    handleDuplicateCheck(field);
  }, blurCheckTimeout);
}

function handleDuplicateCheck(field: string) {
  const ongoingCheckOrInteraction =
    isCheckingDuplicate.value || isSelectingFromModal || showPhoneCard.value || showEmailCard.value;
  if (ongoingCheckOrInteraction) return;

  if (checkTimeoutId) {
    clearTimeout(checkTimeoutId);
    checkTimeoutId = null;
  }

  checkTimeoutId = performDuplicateCheck(field) as unknown as ReturnType<typeof setTimeout>;
}

function performDuplicateCheck(field: string) {
  const currentValue = model.value?.trim();

  if (!currentValue) return;

  const normalizedValue: string =
    field === 'phone' ? formatPhoneNumber(currentValue) || currentValue : currentValue.toLowerCase();

  if (lastCheckedValue.value === normalizedValue) return;

  requestCounter++;
  const currentRequestId = requestCounter;

  const fieldKey = field as 'phone' | 'email';
  results.value[fieldKey] = [];
  if (fieldKey === 'phone') showPhoneCard.value = false;
  if (fieldKey === 'email') showEmailCard.value = false;

  const PAYLOAD = {
    entity: 'people',
    field,
    value: normalizedValue,
    page: 1,
    per_page: ROWS_PER_PAGE,
  };

  isCheckingDuplicate.value = true;
  lastCheckedValue.value = normalizedValue;

  checkDuplicatePersonService(PAYLOAD)
    .then((result: any) => {
      if (currentRequestId !== requestCounter) return;
      const rows = Array.isArray(result) ? result : result?.data ?? [];
      if (!rows.length) {
        if (fieldKey === 'phone') showPhoneCard.value = false;
        if (fieldKey === 'email') showEmailCard.value = false;
        return;
      }

      results.value[fieldKey] = rows;
      lastDuplicateValueByField.value[fieldKey] = normalizedValue;
      duplicateTotal.value = Array.isArray(result) ? result.length : result?.total ?? 0;
      duplicateFirst.value = 0;

      if (fieldKey === 'phone') showPhoneCard.value = true;
      if (fieldKey === 'email') showEmailCard.value = true;
    })
    .finally(() => {
      if (currentRequestId !== requestCounter) return;
      isCheckingDuplicate.value = false;
    });
}

function setPersonData(field: string, data: PersonResult) {
  clearChecksAndPendingRequests();

  isSelectingFromModal = true;

  showPhoneCard.value = false;
  showEmailCard.value = false;
  duplicateTotal.value = 0;
  duplicateFirst.value = 0;
  lastDuplicateValueByField.value = { phone: '', email: '' };

  contactData.value = data;
  personId.value = data._id.$oid;

  lastCheckedValue.value = '';

  const modalCloseTimeout = 50;
  setTimeout(() => {
    isSelectingFromModal = false;
  }, modalCloseTimeout);
}

function handleCloseModal() {
  showPhoneCard.value = false;
  showEmailCard.value = false;
  lastCheckedValue.value = '';
  duplicateTotal.value = 0;
  duplicateFirst.value = 0;
  lastDuplicateValueByField.value = { phone: '', email: '' };
}

function onDuplicatePageChange(event: { page: number; first: number; rows: number }) {
  const field: 'phone' | 'email' = showPhoneCard.value ? 'phone' : 'email';
  fetchDuplicatePage(event.page + 1, field, { paginationFirst: event.first });
}

function fetchDuplicatePage(page: number, field: 'phone' | 'email', options: { paginationFirst?: number } = {}) {
  const value = lastDuplicateValueByField.value[field];
  if (!value) return;

  requestCounter++;
  const currentRequestId = requestCounter;

  isCheckingDuplicate.value = true;

  checkDuplicatePersonService({
    entity: 'people',
    field,
    value,
    page,
    per_page: ROWS_PER_PAGE,
  })
    .then((result: any) => {
      if (currentRequestId !== requestCounter) return;

      const rows = Array.isArray(result) ? result : result?.data ?? [];
      results.value[field] = rows;
      duplicateTotal.value = Array.isArray(result) ? result.length : result?.total ?? 0;
      duplicateFirst.value = options.paginationFirst ?? (page - 1) * ROWS_PER_PAGE;
    })
    .finally(() => {
      if (currentRequestId !== requestCounter) return;

      isCheckingDuplicate.value = false;
    });
}
</script>

<template>
  <div>
    <AFieldPhone
      v-if="props.field.name === 'phone'"
      aria-describedby="person-help"
      v-model="model"
      :error
      :field
      :required
      :debounceTime="0"
      :inProgress="isCheckingDuplicate"
      @blur="handleBlur('phone')"
    />

    <AFieldEmail
      v-else-if="props.field.name === 'email'"
      aria-describedby="person-help"
      v-model="model"
      :error
      :field
      :required
      :debounceTime="0"
      :inProgress="isCheckingDuplicate"
      @blur="handleBlur('email')"
    />

    <Teleport to="body">
      <AFieldPersonCard
        v-if="showPhoneCard"
        :hasConfirmationStep="field.settings?.hasConfirmationStep"
        :loading="isCheckingDuplicate"
        :mode="field.settings.mode"
        :paginationFirst="duplicateFirst"
        :paginationRows="ROWS_PER_PAGE"
        :paginationTotalRecords="duplicateTotal"
        :results="results.phone"
        @onClose="handleCloseModal"
        @onUsePerson="(data: any) => setPersonData(field.name, data)"
        @page="onDuplicatePageChange"
      />

      <AFieldPersonCard
        v-else-if="showEmailCard"
        :hasConfirmationStep="field.settings?.hasConfirmationStep"
        :loading="isCheckingDuplicate"
        :mode="field.settings.mode"
        :paginationFirst="duplicateFirst"
        :paginationRows="ROWS_PER_PAGE"
        :paginationTotalRecords="duplicateTotal"
        :results="results.email"
        @onClose="handleCloseModal"
        @onUsePerson="(data: any) => setPersonData(field.name, data)"
        @page="onDuplicatePageChange"
      />
    </Teleport>
  </div>
</template>
